import { IconArrowExport } from '@nxsys/components/AppIconSvg';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useCreditInvoice } from '@nxsys/hooks/api/invoice/useInvoiceApi';
import {
  useApproveTimesheet,
  useCancelTimesheet,
  useGenerateInvoice,
} from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import { GetAgencyTimesheetListParams } from '@nxsys/store/slices/agency/types';
import { GenerateInvoiceParams } from '@nxsys/types/api';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showMessage } from '@nxsys/utils/message';
import { debounce, toNumber } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import CreditInvoiceModal from '../../invoices/components/CreditInvoiceModal';
import GenerateInvoiceModal, {
  GenerateInvoiceModalValues,
} from '../../timesheets/components/GenerateInvoiceModal';
import { getTimesheetAgencyColumns } from '../../timesheets/listing/column';
import { TimesheetStatusEnum } from '@nxsys/constants/AppEnums';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import { useMobile } from '@nxsys/hooks/useMobile';
import { formatMoney } from '@nxsys/utils/number-helper';
import { showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { ExportToCsv } from 'export-to-csv';


const timesheetStatusOptions = [
  { label: 'Awaiting Invoicing', value: 'AWAITING_INVOICING' },
  { label: 'Invoiced', value: 'INVOICED' },
  { label: 'Credited', value: 'CREDITED' },
];

const TimesheetAgency = ({
  data,
  status,
  approveStatus,
  hasBorder = true,
}: {
  data: IAgency;
  status?: string;
  approveStatus?: string;
  hasBorder?: boolean;
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();
  const { isMobile } = useMobile();

  const dispatch = useDispatch<AppDispatch>();
  const { loading, list, pagination, queryParams } = useSelector(
    (state: AppState) => state.agencyStore.agencyTimesheetList,
  );
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const [timesheetStatus, setTimesheetStatus] = useState<string | null>(null);

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);

  useEffect(() => {
    if (approveStatus) {
      handleFilter({ approveStatus, invoiceStatus: undefined });
    } else if (status) {
      if (status === 'ALL') {
        handleFilter({ invoiceStatus: undefined, approveStatus: undefined });
      } else {
        handleFilter({ invoiceStatus: status });
      }
    } else {
      getTimeSheetList({
        page: 0,
        size: 20,
      });
    }
  }, [status, approveStatus]);

  const getTimeSheetList = (params: GetAgencyTimesheetListParams) => {
    const newParams = {
      ...params,
    };
    dispatch(
      agencyAction.getAgencyTimesheetListById({
        id: data.id,
        params: newParams,
      }),
    );
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetAgencyTimesheetListParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/timesheets/${id}`, {
        query: {
          from: `/agency/${data.id}`,
        },
      });
    },
    [navigate],
  );

  const searchDebounce = useCallback(
    debounce((search: string) => handleFilter({ search }), 500),
    [handleFilter],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    searchDebounce(searchValue);
  };

  const [openGenerateInvoiceModal, setOpenGenerateInvoiceModal] =
    useState(false);

  const toggleGenerateInvoiceModal = () => {
    setOpenGenerateInvoiceModal(!openGenerateInvoiceModal);
  };

  const { run: generateInvoice, loading: isGenerateInvoicePending } =
    useGenerateInvoice();

  const handleOpenGenerateInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return false;
    }
    setOpenGenerateInvoiceModal(true);
  };

  const handleGenerate = (values: GenerateInvoiceModalValues) => {
    const params: GenerateInvoiceParams = {
      ids: selectedRows.map((item) => item.id),
      dueDateTimesheet: moment(values.dueDate).toISOString(),
      invoicedDate: moment(values.invoicedDate).toISOString(),
    };
    generateInvoice(params).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.generateInvoiceSuccess'],
      });
      toggleGenerateInvoiceModal();
      refetchTimesheetList();
    });
  };

  const [openCreditInvoice, setOpenCreditInvoice] = useState(false);
  const toggleCreditInvoice = () => setOpenCreditInvoice(!openCreditInvoice);

  const handleOpenCreditInvoice = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    setOpenCreditInvoice(true);
  };

  const { run: creditInvoiceRun, loading: isCreditInvoicePending } =
    useCreditInvoice();

  const handleCreditInvoice = async (date: string) => {
    if (selectedRows.length === 0) return;
    const res = await creditInvoiceRun({
      ids: selectedRows.map((row) => row.id),
      creditDate: date,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['invoices.creditInvoiceSuccess'],
      });
      toggleCreditInvoice();
      refetchTimesheetList();
    } else {
      showMessage({
        type: 'error',
        content: messages['invoices.creditInvoiceFailed'],
      });
    }
  };

  const { run: approveTimesheet, loading: isApproveTimesheetPending } =
    useApproveTimesheet();
  const handleApprove = useCallback(
    (record: TimeSheetDataItem) => {
      approveTimesheet({ ids: [record.id] }).then(() => {
        showMessage({
          type: 'success',
          content: messages['timesheet.approveSuccess'],
        });
        refetchTimesheetList();
        setSelectedRows([]);
      });
    },
    [selectedRows],
  );

  const { run: cancelTimesheet, loading: isCancelTimesheetPending } =
    useCancelTimesheet();
  const handleCancel = (record: TimeSheetDataItem) => {
    const isValid =
      record?.approveStatus !== TimesheetStatusEnum.CANCEL &&
      !record?.newDraftPayroll &&
      !record?.newPayroll;
    if (!isValid) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToCancel'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['timesheet.cancel'],
      paragraph: formatMessage(
        { id: 'timesheet.cancelConfirm' },
        {
          timesheetList: (
            <span style={{ color: theme.palette.primary.main }}>
              {record.id}
            </span>
          ),
        },
      ),
      onConfirm: async () => {
        await cancelTimesheet([record.id]);
        showMessage({
          type: 'success',
          content: messages['timesheet.cancelSuccess'],
        });
        refetchTimesheetList();
      },
    });
  };

  const handleExport = (data: TimeSheetDataItem[]) => {
    const dataExport = data.map((item) => {
      return {
        status: item?.status || '',
        id: item?.id,
        subcontractorId: item?.subcontractorRef?.id || '',
        invoicedDate: item?.invoicedDate
          ? moment(item?.invoicedDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        batch: item?.batch || '',
        name: `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname || ''
        }`,
        startDate: item?.startDate
          ? moment(item?.startDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        endDate: item?.endDate
          ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        totalNet: formatMoney(
          toNumber(item?.totalNet) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalVat: formatMoney(
          toNumber(item?.totalVat) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalPrices: formatMoney(
          toNumber(item?.totalPrices) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
      };
    });

    const headers = [
      'Status',
      'Timesheet ID',
      'Employee ID',
      'Date',
      'Batch #',
      'Name',
      'Week Start Date',
      'Week End Date',
      'Net',
      'VAT',
      'Total',
    ];

    const exportName = csvConfigExport(`Timesheet-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  return (
    <AppCard
      title={hasBorder ? messages['common.timesheet'] : undefined}
      hasBorder={hasBorder}
    >
      <AppsHeader>
        <div className="flex justify-between w-full">
          <div className="flex gap-x-2 max-sm:w-full">
            <AppInputSearch onChange={handleSearch} />
            {!status && (
              <AppSelect
                options={timesheetStatusOptions}
                onChange={(option) => {
                  if (!option) {
                    handleFilter({ invoiceStatus: undefined });
                  } else {
                    handleFilter({ invoiceStatus: option.value });
                  }
                  setTimesheetStatus(option?.value ?? null);
                }}
                placeholder={messages['form.selectStatus']}
                className="max-sm:!min-w-[unset] !w-[200px]"
              />
            )}
          </div>
          {timesheetStatus === 'AWAITING_INVOICING' && (
            <AppButton
              name={messages['timesheet.generateInvoice']}
              onClick={handleOpenGenerateInvoice}
              loading={isGenerateInvoicePending}
              disabled={isGenerateInvoicePending}
            />
          )}
          {timesheetStatus === 'INVOICED' && (
            <AppButton
              name={messages['invoices.creditInvoice']}
              onClick={handleOpenCreditInvoice}
            />
          )}
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.export']}
            onClick={handleExportSelected}
            leftIcon={<IconArrowExport />}
          />
        </div>
      </AppsHeader>
      <AppTableContainer
        rowKey="id"
        data={list ?? []}
        loading={
          loading || isApproveTimesheetPending || isCancelTimesheetPending
        }
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        scroll={{ x: 2200, y: 700 }}
        titleEmpty={messages['timesheet.empty']}
        columns={getTimesheetAgencyColumns({
          messages,
          payrollCountry,
          handleView,
          handleApprove,
          handleReject: handleCancel,
          isMobile,
          theme,
        })}
        onChangePagination={onChangePagination}
        rowSelection={{
          type: 'checkbox',
          onChange: (_: React.Key[], selectedRows: any[]) => {
            setSelectedRows(selectedRows);
          },
        }}
      />
      <GenerateInvoiceModal
        isOpen={openGenerateInvoiceModal}
        toggle={toggleGenerateInvoiceModal}
        onGenerate={handleGenerate}
        isGenerateInvoicePending={isGenerateInvoicePending}
      />
      <CreditInvoiceModal
        isOpen={openCreditInvoice}
        toggle={toggleCreditInvoice}
        onSubmit={handleCreditInvoice}
        isCreditInvoicePending={isCreditInvoicePending}
      />
    </AppCard>
  );
};

export default React.memo(TimesheetAgency);
